/**
 * The last thing between a render throw and a blank page.
 *
 * F-008. No file in this repository implemented `componentDidCatch` or
 * `getDerivedStateFromError`, so any throw during render unmounted the whole tree under
 * `src/main.tsx`: a white page, the operator's session gone from view, and no way back but a
 * manual reload. Sentry was already initialised, so the error was reported — the person staring at
 * the blank page was the only one not told.
 *
 * ⚠️ `<Suspense>` in `src/App.tsx` does not cover this. It catches *loading*, not throwing.
 *
 * ⚠️ **It mounts INSIDE `AuthProvider`, and that placement is the point.** Above it, a crash would
 * take the session with it and land the operator on the login screen, which reads as "I was logged
 * out" rather than "that screen broke". Inside, the session survives, and the fallback is a page
 * they can reload or navigate away from.
 *
 * The raw message is deliberately not shown. It is written by developers for developers and can
 * carry a connection string, a token or a customer's data as readily as a stack frame; Sentry is
 * where it belongs. What the operator gets is that this screen failed, that it has been reported,
 * and a button.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Surface } from '@nexvia-logistics/ui';

interface Props {
    children: ReactNode;
}

interface State {
    failed: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { failed: false };

    /** Runs during render, so it may only compute state — the reporting happens below. */
    static getDerivedStateFromError(): State {
        return { failed: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        Sentry.captureException(error, {
            extra: { componentStack: info.componentStack },
        });
    }

    render() {
        if (!this.state.failed) return this.props.children;

        return (
            <div className="grid min-h-[60vh] place-items-center p-6">
                <Surface variant="panel" padding="lg" className="max-w-md text-center">
                    {/*
                     * role="alert" rather than a heading: this appears without the operator doing
                     * anything, and a screen reader should say so rather than wait to be asked.
                     */}
                    <div role="alert">
                        <h2 className="m-0 text-ui-h2 font-semibold text-on-surface">
                            Esta pantalla dejó de funcionar
                        </h2>
                        <p className="m-0 mt-2 text-ui-body text-on-surface-variant">
                            El error ya fue reportado al equipo. Tu sesión sigue abierta, así que podés
                            recargar o volver a otra sección.
                        </p>
                    </div>
                    <div className="mt-5 flex justify-center">
                        <Button showArrow={false} onClick={() => location.reload()}>
                            Recargar
                        </Button>
                    </div>
                </Surface>
            </div>
        );
    }
}
